import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";
import { DISPLAY_LOCALE } from "@how/ui";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export const DAY_LABEL = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
} as const;
export type Weekday = keyof typeof DAY_LABEL;

/** "19:00" as "7:00 PM" in the reader's locale; a wall-clock time at the campus. */
export function formatMeetingTime(time: string | null): string | null {
  if (time === null) return null;
  const [h, m] = time.split(":");
  if (h === undefined || m === undefined) return time;
  return new Date(2000, 0, 1, Number(h), Number(m)).toLocaleTimeString(DISPLAY_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
  });
}

const mineSchema = z.object({
  membershipId: z.string(),
  status: z.enum(["requested", "active", "left", "declined"]),
  role: z.enum(["member", "leader"]),
  requestedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  requestNote: z.string().nullable(),
});

const groupSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  campus: z.object({ id: z.string(), name: z.string() }).nullable(),
  meetingDay: z
    .enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"])
    .nullable(),
  meetingTime: z.string().nullable(),
  meetingLocation: z.string().nullable(),
  visibility: z.enum(["public", "unlisted", "private"]),
  joiningRule: z.enum(["open", "request", "closed"]),
  capacity: z.number().nullable(),
  activeMembers: z.number(),
  ageMin: z.number().nullable(),
  ageMax: z.number().nullable(),
  leaders: z.array(z.string()),
  membersConfidential: z.boolean(),
  /** Co-members by name, for an active member of a group that is not confidential; else null. */
  members: z.array(z.object({ name: z.string(), leader: z.boolean() })).nullable(),
  mine: mineSchema.nullable(),
  led: z.boolean(),
});
export type FoundGroup = z.infer<typeof groupSchema>;
export type Mine = z.infer<typeof mineSchema>;

/**
 * Every active group the member may see, with where they stand in it (ADR 0040, task 5). One call; the screen
 * splits it into mine, the ones I lead, and the rest. 42501 means the church has no groups module.
 */
export function useFindGroups() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["find-groups", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<FoundGroup[]> => {
      const { data, error } = await supabase.rpc("find_groups", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return z.array(groupSchema).parse(data);
    },
  });
}

const rosterMemberSchema = z.object({
  membershipId: z.string(),
  personId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  role: z.enum(["member", "leader"]),
  status: z.enum(["requested", "active", "left", "declined"]),
  requestedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  leftAt: z.string().nullable(),
  requestNote: z.string().nullable(),
});
const rosterSchema = z.object({
  season: z.object({ id: z.string(), name: z.string(), isCurrent: z.boolean() }),
  members: z.array(rosterMemberSchema),
  childrenHidden: z.number(),
});
export type RosterMember = z.infer<typeof rosterMemberSchema>;

export function memberName(m: RosterMember): string {
  return `${m.preferredName ?? m.firstName} ${m.lastName}`;
}

/** The roster of a group the member leads: the same function staff use, scoped by the database. */
export function useLedRoster(groupId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["led-roster", tenantId, groupId],
    enabled: groupId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("group_roster", { p_group_id: groupId ?? "" });
      if (error) throw error;
      return rosterSchema.parse(data);
    },
  });
}

function useInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["find-groups", tenantId] });
    await client.invalidateQueries({ queryKey: ["led-roster", tenantId] });
  };
}

export function useJoinGroup() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: { groupId: string; note: string }) => {
      const { error } = await supabase.rpc("join_group", {
        p_tenant_id: tenantId ?? "",
        p_group_id: input.groupId,
        ...(input.note.trim() === "" ? {} : { p_note: input.note }),
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useLeaveGroup() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase.rpc("leave_group", {
        p_tenant_id: tenantId ?? "",
        p_group_id: groupId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useDecideRequest() {
  const supabase = useSupabase();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: { membershipId: string; approve: boolean }) => {
      const { error } = await supabase.rpc("decide_group_request", {
        p_membership_id: input.membershipId,
        p_approve: input.approve,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
